/** @jsx jsx */
/* eslint react/prop-types: 0 */
import { jsx, Styled, Flex, Embed } from "theme-ui"
import { graphql, Link } from "gatsby"
import Img from "gatsby-image"
import VisuallyHidden from "@reach/visually-hidden"

import Layout, { Container, Row, Col } from "components/layout"
import Card from "components/card"
import LogoWall from "components/logo-wall"
import Infographic from "components/infographic"
import Panel from "components/panel"
import Paper from "components/paper"
import StarBurst from "components/star-burst"
import Tile from "components/tile"
import { Blue, Large } from "components/type"
import LocalizedButton from "components/localized-button"

import useLocale from "hooks/use-locales"

import Arrow from "components/svgs/arrow"
import Blotch from "components/svgs/blotch"
import Quote from "components/svgs/quote"
import Wave from "components/svgs/wave"

import BarGraph from "components/svgs/bar-graph"
import Folder from "components/svgs/folder"
import Dots from "components/svgs/dots"

import SupportingEachother from "../partials/home/supporting-eachother"

const IndexPage = ({ data }) => {
  const { t, locale } = useLocale()
  let logo = data.logoEn.childImageSharp.fluid
  if (locale == "fr") {
    logo = data.logoFr.childImageSharp.fluid
  }
  return (
    <Layout pageTitle={`${t("title")} 2019`} description={t("description")}>
      {/** Hero */}
      <Panel bg="primary" color="background" sx={{ mt: -4 }} space={[4, 5, 6]}>
        <Container wide>
          <Img fluid={logo} alt="" />
          <Row
            sx={{
              flexDirection: ["column", "row"],
              alignItems: ["center", "flex-start"],
              mt: [4, 5, 6],
              justifyContent: "space-between",
            }}
          >
            <Col sx={{ width: ["100%", "40%"] }}>
              <Styled.h1
                sx={{
                  color: "background",
                  mb: 0,
                  textTransform: "uppercase",
                  textAlign: ["center", "left"],
                }}
              >
                {t("title")}
              </Styled.h1>
              <Styled.h3
                as="p"
                sx={{ color: "background", textAlign: ["center", "left"] }}
              >
                2019
              </Styled.h3>
            </Col>
            <Col sx={{ width: ["100%", "55%"] }}>
              <Large sx={{ textAlign: ["center", "right"] }}>
                {t("thanks")}
              </Large>
            </Col>
          </Row>
        </Container>
      </Panel>
      {/** Leadership Message */}
      <Panel bg="muted" color="text" space={[4, 5]}>
        <Container wide>
          <Row sx={{ flexWrap: "wrap" }}>
            <Col sx={{ width: ["100%", "100%", "55%"] }}>
              <StarBurst as="h2">{t("leadership.title")}</StarBurst>
              <Styled.p>{t("leadership.intro")}</Styled.p>
            </Col>
            <Col sx={{ width: ["100%", "100%", "45%"], textAlign: "center" }}>
              <Flex
                sx={{
                  alignItems: ["center", "flex-end"],
                  flexDirection: "column",
                  height: "100%",
                  justifyContent: "flex-start",
                }}
              >
                <Row sx={{ width: "100%", flexWrap: "wrap" }}>
                  <Col sx={{ width: ["100%", "50%"] }}>
                    <Img
                      sx={{ mb: 3, mx: "auto" }}
                      fixed={data.lorne.childImageSharp.fixed}
                      alt=""
                    />
                    <Styled.p sx={{ fontSize: [3, 4], mb: 0 }}>
                      Lorne Shiff
                    </Styled.p>
                    <Styled.p sx={{ mt: 1 }}>{t("leadership.board")}</Styled.p>
                    <Img
                      sx={{ mb: 3, mx: "auto" }}
                      fixed={data.lorneSignature.childImageSharp.fixed}
                      alt=""
                    />
                  </Col>
                  <Col sx={{ width: ["100%", "50%"] }}>
                    <Img
                      sx={{ mb: 3, mx: "auto" }}
                      fixed={data.dave.childImageSharp.fixed}
                      alt=""
                    />
                    <Styled.p sx={{ fontSize: [3, 4], mb: 0 }}>
                      Dave Prowten
                    </Styled.p>
                    <Styled.p sx={{ mt: 1 }}>
                      {t("leadership.president")}
                    </Styled.p>
                    <Img
                      sx={{ mb: 3, mx: "auto" }}
                      fixed={data.daveSignature.childImageSharp.fixed}
                      alt=""
                    />
                  </Col>
                </Row>
                <Wave sx={{ width: "100%", height: "auto", mb: 4 }} />
                <LocalizedButton to="/leadership-message" sx={{ mt: "auto" }}>
                  {t("leadership.button")}
                </LocalizedButton>
              </Flex>
            </Col>
          </Row>
        </Container>
      </Panel>
      {/** Supporting Eachother */}
      <SupportingEachother />
      {/** Video */}
      {locale !== "fr" ? (
        <Embed src="https://www.youtube.com/embed/JvJdlsIvCC8" />
      ) : null}
      {/** Accelerating Research */}
      <Panel
        bg="background"
        color="text"
        space={[4, 5]}
        sx={{ position: "relative" }}
      >
        <Container wide>
          <Arrow
            sx={{
              position: "absolute",
              left: 0,
              top: [1, 3, 1],
              maxWidth: ["6rem", "10rem"],
              height: "auto",
              transform: "translateX(-50%)",
            }}
          />
          <Styled.h1 as="h2" sx={{ color: "primary", mt: 0, mb: 0 }}>
            {t("research.title")}
          </Styled.h1>
          <Styled.p sx={{ color: "dark" }}>
            {t("research.intro")}{" "}
            <span sx={{ color: "primary" }}>{t("research.bold")}</span>
          </Styled.p>
          <Row sx={{ flexWrap: ["wrap", "nowrap"], mb: 5 }}>
            <Col sx={{ width: ["100%", "75%"] }}>
              <Styled.h2 as="h3">{t("research.one.title")}</Styled.h2>
              <Styled.p>{t("research.one.intro")}&hellip;</Styled.p>
              <Styled.p>
                <LocalizedButton
                  to="/accelerating-research-1"
                  sx={{
                    display: ["block", "inline-block"],
                    width: ["100%", "auto"],
                  }}
                >
                  {t("read")}{" "}
                  <VisuallyHidden>{t("research.one.title")}</VisuallyHidden>{" "}
                </LocalizedButton>
              </Styled.p>
            </Col>
            <Col sx={{ width: ["80%", "25%"], mx: ["auto", 0] }}>
              <Img
                fluid={data.research.childImageSharp.fluid}
                alt=""
                sx={{ width: "100%", height: "auto", borderRadius: "default" }}
              />
            </Col>
          </Row>
          <Row sx={{ flexWrap: "wrap" }}>
            <Col sx={{ width: ["100%", "50%"] }}>
              <Card
                title={t("research.two.title")}
                fluid={data.hypoglycemia.childImageSharp.fluid}
                to="/"
              >
                <Styled.p>{t("research.two.intro")}</Styled.p>
                <Styled.p>
                  <LocalizedButton
                    to="/accelerating-research-2"
                    sx={{
                      display: ["block", "inline-block"],
                      width: ["100%", "auto"],
                    }}
                  >
                    {t("read")}{" "}
                    <VisuallyHidden>{t("research.two.title")}</VisuallyHidden>
                  </LocalizedButton>
                </Styled.p>
              </Card>
            </Col>
            <Col sx={{ width: ["100%", "50%"] }}>
              <Card
                title={t("research.three.title")}
                fluid={data.trialnet.childImageSharp.fluid}
                to="/"
              >
                <Styled.p>{t("research.three.intro")}&hellip;</Styled.p>
                <Styled.p>
                  <LocalizedButton
                    to="/accelerating-research-3"
                    sx={{
                      display: ["block", "inline-block"],
                      width: ["100%", "auto"],
                    }}
                  >
                    {t("read")}{" "}
                    <VisuallyHidden>{t("research.three.title")}</VisuallyHidden>
                  </LocalizedButton>
                </Styled.p>
              </Card>
            </Col>
          </Row>
          <Arrow
            flipX
            sx={{
              position: "absolute",
              right: [3, 2 - 1],
              bottom: [1, 2, 5],
              maxWidth: ["6rem", "6rem", "16rem"],
              height: "auto",
            }}
          />
        </Container>
      </Panel>
      {/** William */}
      <Panel bg="muted" color="text" space={[5, 6]}>
        <Container wide>
          <Row
            sx={{
              flexDirection: ["column", "row"],
              justifyContent: "space-between",
            }}
          >
            <Col
              sx={{
                width: ["100%", "40%"],
                justifyContent: ["center", "flex-start"],
              }}
            >
              <Quote sx={{ mb: 3, width: "3rem", height: "auto" }} />
              <Paper bg="primary" color="background" holeColor="muted">
                {t("quote.william.text")}
              </Paper>
            </Col>
            <Col
              sx={{
                width: ["100%", "50%"],
                justifyContent: ["center", "flex-start"],
              }}
            >
              <Img
                fixed={data.william.childImageSharp.fixed}
                alt=""
                sx={{ mb: 3 }}
              />
              <Styled.h2 sx={{ mb: 0 }}>William</Styled.h2>
              <Large>{t("quote.william.cite")}</Large>
            </Col>
          </Row>
        </Container>
      </Panel>
      {/** Mobilizing for Change */}
      <Panel bg="primary" color="background" space={[4, 5]}>
        <Container>
          <Styled.h2>{t("change.title")}</Styled.h2>
          <Styled.p sx={{ mb: 5 }}>{t("change.intro")}</Styled.p>
        </Container>
        <Container wide>
          <Row sx={{ flexWrap: "wrap" }}>
            <Col sx={{ width: ["100%", "100%", "33.333%"] }}>
              <Img
                sx={{ borderRadius: "default", mb: 3 }}
                fluid={data.arielle.childImageSharp.fluid}
                alt=""
              />
              <Styled.h3>{t("change.one.title")}</Styled.h3>
              <Styled.p>
                <LocalizedButton variant="ghost" as={Link} to="/mobilizing-1">
                  {t("change.one.button")}
                </LocalizedButton>
              </Styled.p>
            </Col>
            <Col sx={{ width: ["100%", "100%", "33.333%"] }}>
              <Img
                sx={{ borderRadius: "default", mb: 3 }}
                fluid={data.noah.childImageSharp.fluid}
                alt=""
              />
              <Styled.h3>{t("change.two.title")}</Styled.h3>
              <Styled.p>
                <LocalizedButton variant="ghost" as={Link} to="/mobilizing-2">
                  {t("change.two.button")}
                </LocalizedButton>
              </Styled.p>
            </Col>
            <Col sx={{ width: ["100%", "100%", "33.333%"] }}>
              <Img
                sx={{ borderRadius: "default", mb: 3 }}
                fluid={data.kevin.childImageSharp.fluid}
                alt=""
              />
              <Styled.h3>{t("change.three.title")}</Styled.h3>
              <Styled.p>
                <LocalizedButton variant="ghost" as={Link} to="/mobilizing-3">
                  {t("change.three.button")}
                </LocalizedButton>
              </Styled.p>
            </Col>
          </Row>
        </Container>
      </Panel>
      {/** Partners */}
      <Panel bg="background" color="text" space={[4, 5]}>
        <Container>
          <Row sx={{ alignItems: "center", flexDirection: ["column", "row"] }}>
            <Col sx={{ width: ["100%", "70%"] }}>
              <StarBurst as="h2" left={true}>
                {t("partner.title")}
              </StarBurst>
            </Col>
            <Col sx={{ width: ["100%", "30%"] }}>
              <Wave sx={{ width: "100%", height: "auto" }} />
            </Col>
          </Row>
          <Blue>{t("partner.strong")}</Blue>
          <Styled.p>{t("partner.thank")}</Styled.p>
        </Container>
        <LogoWall />
        <Container wide>
          <Row sx={{ flexWrap: "wrap" }}>
            <Col sx={{ width: ["100%", "50%"], mb: 5 }}>
              <Img
                fluid={data.pizzaHut.childImageSharp.fluid}
                alt=""
                sx={{ mb: 3 }}
              />
              <Styled.h3>{t("partner.one.short")}</Styled.h3>
              <Styled.p>{t("partner.one.intro")}&hellip;</Styled.p>
              <Styled.p>
                <LocalizedButton as={Link} to="/partner-1">
                  {t("read")}{" "}
                  <VisuallyHidden>{t("partner.one.short")}</VisuallyHidden>
                </LocalizedButton>
              </Styled.p>
            </Col>
            <Col sx={{ width: ["100%", "50%"], mb: 5 }}>
              <Img
                fluid={data.bostonPizza.childImageSharp.fluid}
                alt=""
                sx={{ mb: 3 }}
              />
              <Styled.h3>{t("partner.two.short")}</Styled.h3>
              <Styled.p>{t("partner.two.intro")}&hellip;</Styled.p>
              <Styled.p>
                <LocalizedButton as={Link} to="/partner-3">
                  {t("read")}{" "}
                  <VisuallyHidden>{t("partner.two.short")}</VisuallyHidden>
                </LocalizedButton>
              </Styled.p>
            </Col>
            <Col sx={{ width: ["100%", "50%"], mb: 5 }}>
              <Img
                fluid={
                  locale === "fr"
                    ? data.rbcFr.childImageSharp.fluid
                    : data.rbc.childImageSharp.fluid
                }
                alt=""
                sx={{ mb: 3 }}
              />
              <Styled.h3>{t("partner.three.short")}</Styled.h3>
              <Styled.p>{t("partner.three.intro")}&hellip;</Styled.p>
              <Styled.p>
                <LocalizedButton as={Link} to="/partner-2">
                  {t("read")}{" "}
                  <VisuallyHidden>{t("partner.three.short")}</VisuallyHidden>
                </LocalizedButton>
              </Styled.p>
            </Col>
            <Col sx={{ width: ["100%", "50%"], mb: 5 }}>
              <Img
                fluid={data.sunlife.childImageSharp.fluid}
                alt=""
                sx={{ mb: 3 }}
              />
              <Styled.h3>{t("partner.four.short")}</Styled.h3>
              <Styled.p>{t("partner.four.intro")}&hellip;</Styled.p>
              <Styled.p>
                <LocalizedButton as={Link} to="/partner-4">
                  {t("read")}{" "}
                  <VisuallyHidden>{t("partner.four.short")}</VisuallyHidden>
                </LocalizedButton>
              </Styled.p>
            </Col>
          </Row>
        </Container>
      </Panel>
      {/** Paper */}
      <Panel bg="primary" space={[4, 5, 6]}>
        <Container wide>
          <Row
            sx={{
              alignItems: "center",
              justifyContent: "center",
              flexDirection: ["column", "row"],
            }}
          >
            <Col sx={{ width: ["80%", "40%"] }}>
              <Paper
                bg="background"
                color="primary"
                rotation={3}
                holeColor="primary"
                cite={`- Ang, ${t("quote.ang.cite")}`}
              >
                {t("quote.ang.text")}
              </Paper>
            </Col>
            <Col sx={{ width: ["80%", "40%"] }}>
              <Paper
                bg="muted"
                color="primary"
                rotation={-3}
                holeColor="primary"
                cite={`- Roxanne, ${t("quote.roxanne.cite")}`}
              >
                {t("quote.roxanne.text")}
              </Paper>
            </Col>
          </Row>
        </Container>
      </Panel>
      {/** Impact */}
      <Panel bg="muted" color="text" space={5} sx={{ mb: -4 }}>
        <Container wide>
          <Row sx={{ mb: 5, alignItems: "flex-end", flexWrap: "wrap" }}>
            <Col sx={{ width: ["100%", "50%"] }}>
              <StarBurst left as="h2" sx={{ my: 0 }}>
                {t("impact.title")}
              </StarBurst>
            </Col>
            <Col sx={{ width: ["100%", "50%"] }}>
              <Blotch
                sx={{
                  height: "auto",
                  width: "50%",
                  display: "block",
                  ml: "auto",
                  mt: "auto",
                }}
              />
            </Col>
          </Row>
          <Row sx={{ flexWrap: "wrap" }}>
            <Col sx={{ width: ["100%", "100%", "33.33%"], mb: [4, 4, 0] }}>
              <Tile sx={{ textAlign: "center" }}>
                <BarGraph
                  sx={{
                    mx: "auto",
                    display: "block",
                    width: "80%",
                    height: "auto",
                  }}
                />
                <Styled.h1 as="p" sx={{ color: "primary", mb: 3 }}>
                  {t("impact.stat.one.value")}
                </Styled.h1>
                <Styled.p sx={{ mb: 0, fontWeight: "bold", color: "accent" }}>
                  {t("impact.stat.one.text")}
                </Styled.p>
              </Tile>
            </Col>
            <Col sx={{ width: ["100%", "100%", "33.33%"], mb: [4, 4, 0] }}>
              <Tile sx={{ textAlign: "center" }}>
                <Folder
                  sx={{
                    mx: "auto",
                    display: "block",
                    width: "80%",
                    height: "auto",
                  }}
                />
                <Styled.h1 as="p" sx={{ color: "primary", mb: 3 }}>
                  {t("impact.stat.two.value")}
                </Styled.h1>
                <Styled.p sx={{ mb: 0, fontWeight: "bold", color: "accent" }}>
                  {t("impact.stat.two.text")}
                </Styled.p>
              </Tile>
            </Col>
            <Col sx={{ width: ["100%", "100%", "33.33%"], mb: [4, 4, 0] }}>
              <Tile sx={{ textAlign: "center" }}>
                <Dots
                  sx={{
                    mx: "auto",
                    display: "block",
                    width: "80%",
                    height: "auto",
                  }}
                />
                <Styled.h1 as="p" sx={{ color: "primary", mb: 3 }}>
                  {t("impact.stat.three.value")}
                </Styled.h1>
                <Styled.p sx={{ mb: 0, fontWeight: "bold", color: "accent" }}>
                  {t("impact.stat.three.text")}
                </Styled.p>
              </Tile>
            </Col>
          </Row>
          <Row sx={{ flexWrap: "wrap" }}>
            <Col sx={{ width: ["100%", "100%", "33.333%"] }}>
              <Infographic
                fixed={data.iconOne.childImageSharp.fixed}
                value={t("impact.stat.four.value")}
              >
                <span
                  dangerouslySetInnerHTML={{
                    __html: t("impact.stat.four.text"),
                  }}
                />
              </Infographic>
            </Col>
            <Col sx={{ width: ["100%", "100%", "33.333%"] }}>
              <Infographic
                fixed={data.iconTwo.childImageSharp.fixed}
                value={t("impact.stat.five.value")}
              >
                {t("impact.stat.five.text")}
              </Infographic>
            </Col>
            <Col sx={{ width: ["100%", "100%", "33.333%"] }}>
              <Infographic
                fixed={data.iconThree.childImageSharp.fixed}
                value={t("impact.stat.six.value")}
              >
                {t("impact.stat.six.text")}
              </Infographic>
            </Col>
          </Row>
          <Flex sx={{ justifyContent: ["center", "flex-end"] }}>
            <Styled.p>
              <LocalizedButton as={Link} to="/impact">
                {t("impact.button")}
              </LocalizedButton>
            </Styled.p>
          </Flex>
        </Container>
      </Panel>
    </Layout>
  )
}

export const query = graphql`
  query IndexPageQuery {
    logoEn: file(relativePath: { eq: "home-logo-en.png" }) {
      childImageSharp {
        fluid(maxWidth: 1920) {
          ...GatsbyImageSharpFluid
        }
      }
    }
    logoFr: file(relativePath: { eq: "home-logo-fr.png" }) {
      childImageSharp {
        fluid(maxWidth: 1920) {
          ...GatsbyImageSharpFluid
        }
      }
    }
    lorne: file(relativePath: { eq: "home-lorne.png" }) {
      childImageSharp {
        fixed(width: 175) {
          ...GatsbyImageSharpFixed
        }
      }
    }
    dave: file(relativePath: { eq: "home-dave.png" }) {
      childImageSharp {
        fixed(width: 175) {
          ...GatsbyImageSharpFixed
        }
      }
    }
    lorneSignature: file(relativePath: { eq: "home-dave-signature.jpg" }) {
      childImageSharp {
        fixed(width: 150) {
          ...GatsbyImageSharpFixed
        }
      }
    }
    daveSignature: file(relativePath: { eq: "home-lorne-signature.jpg" }) {
      childImageSharp {
        fixed(width: 150) {
          ...GatsbyImageSharpFixed
        }
      }
    }
    support: file(relativePath: { eq: "home-supporting-eachother.jpg" }) {
      childImageSharp {
        fluid(maxWidth: 1920) {
          ...GatsbyImageSharpFluid
        }
      }
    }
    research: file(relativePath: { eq: "home-accelerated-research.jpg" }) {
      childImageSharp {
        fluid(maxWidth: 290) {
          ...GatsbyImageSharpFluid
        }
      }
    }
    hypoglycemia: file(
      relativePath: { eq: "home-hypoglycemia-prevention.jpg" }
    ) {
      childImageSharp {
        fluid(maxWidth: 500) {
          ...GatsbyImageSharpFluid
        }
      }
    }
    trialnet: file(relativePath: { eq: "home-trialnet.jpg" }) {
      childImageSharp {
        fluid(maxWidth: 500) {
          ...GatsbyImageSharpFluid
        }
      }
    }
    william: file(relativePath: { eq: "home-william.jpg" }) {
      childImageSharp {
        fixed(width: 270) {
          ...GatsbyImageSharpFixed
        }
      }
    }
    arielle: file(relativePath: { eq: "home-arielle.jpg" }) {
      childImageSharp {
        fluid(maxWidth: 500) {
          ...GatsbyImageSharpFluid
        }
      }
    }
    noah: file(relativePath: { eq: "home-noah.jpg" }) {
      childImageSharp {
        fluid(maxWidth: 500) {
          ...GatsbyImageSharpFluid
        }
      }
    }
    kevin: file(relativePath: { eq: "home-kevin.jpg" }) {
      childImageSharp {
        fluid(maxWidth: 500) {
          ...GatsbyImageSharpFluid
        }
      }
    }
    pizzaHut: file(relativePath: { eq: "home-logo-pizza-hut.jpg" }) {
      childImageSharp {
        fluid(maxWidth: 425) {
          ...GatsbyImageSharpFluid
        }
      }
    }
    bostonPizza: file(relativePath: { eq: "home-logo-boston-pizza.jpg" }) {
      childImageSharp {
        fluid(maxWidth: 425) {
          ...GatsbyImageSharpFluid
        }
      }
    }
    rbc: file(relativePath: { eq: "home-logo-rbc.jpg" }) {
      childImageSharp {
        fluid(maxWidth: 425) {
          ...GatsbyImageSharpFluid
        }
      }
    }
    rbcFr: file(relativePath: { eq: "home-logo-rbc-fr.jpg" }) {
      childImageSharp {
        fluid(maxWidth: 425) {
          ...GatsbyImageSharpFluid
        }
      }
    }
    sunlife: file(relativePath: { eq: "home-logo-sunlife.jpg" }) {
      childImageSharp {
        fluid(maxWidth: 425) {
          ...GatsbyImageSharpFluid
        }
      }
    }
    iconOne: file(relativePath: { eq: "home-icon-1.png" }) {
      childImageSharp {
        fixed(width: 150) {
          ...GatsbyImageSharpFixed
        }
      }
    }
    iconTwo: file(relativePath: { eq: "home-icon-2.png" }) {
      childImageSharp {
        fixed(width: 150) {
          ...GatsbyImageSharpFixed
        }
      }
    }
    iconThree: file(relativePath: { eq: "home-icon-3.png" }) {
      childImageSharp {
        fixed(width: 150) {
          ...GatsbyImageSharpFixed
        }
      }
    }
  }
`

export default IndexPage
